import { useState } from 'react';
import type { SummaryAnswer, SummaryRecovery, SummaryRows, SummarySeries, SummarySheetOption } from '@agio/core';
import { SUMMARY_SERIES } from '@agio/core/domain';
import { fmtMonth, fmtPct } from '../../utils.js';
import { Table, THeadRow, TH, TD, TR } from '../ui/table.js';

const SERIES_HE: Record<SummarySeries, string> = {
  monthly_return: 'תשואה חודשית',
  cumulative_return: 'תשואה מצטברת',
  benchmark_monthly: "בנצ'מרק חודשי",
  benchmark_cumulative: "בנצ'מרק מצטבר",
};
const REQUIRED: SummarySeries[] = ['monthly_return', 'cumulative_return'];

type Rows = Record<SummarySeries, number | null>;
const noRows = (): Rows => ({ monthly_return: null, cumulative_return: null, benchmark_monthly: null, benchmark_cumulative: null });

/**
 * Every series here is a return, so its preview is a percentage whatever its
 * size (a 250% cumulative return is 2.5). A balance row picked by mistake then
 * shows as an absurd percentage — a visible hint it is the wrong row.
 */
const fmtValue = (v: number | null) => (v === null ? '--' : fmtPct(v, 2));

const sameRows = (a: Rows, b: Rows) => SUMMARY_SERIES.every((s) => a[s] === b[s]);

/**
 * The summary sheet's rows did not carry their usual names. The system may
 * propose which rows hold the portfolio's monthly and cumulative return and
 * the benchmark — a proposal it has already checked: the monthly row, chained
 * month by month, reproduces the cumulative row. The admin confirms, picks
 * other rows (the server checks them the same way), or says not to read a
 * summary sheet, in which case the return is computed from the funds. The
 * answer is kept for this client only. Nothing has loaded yet.
 */
export function SummaryConfirm({
  recovery,
  busy,
  onConfirm,
  onCancel,
}: {
  recovery: SummaryRecovery;
  busy: boolean;
  onConfirm: (answer: SummaryAnswer) => void;
  onCancel: () => void;
}) {
  const start: SummaryRows | null = recovery.proposal ?? recovery.previous;
  const [sheet, setSheet] = useState<string>(start?.sheet ?? recovery.sheets[0]?.sheet ?? '');
  const [rows, setRows] = useState<Rows>(start ? { ...start.rows } : noRows());
  const option: SummarySheetOption | undefined = recovery.sheets.find((s) => s.sheet === sheet);

  const pickSheet = (s: string) => {
    setSheet(s);
    setRows(start && start.sheet === s ? { ...start.rows } : noRows());
  };
  const missing = REQUIRED.filter((s) => rows[s] === null);
  const benchHalf = (rows.benchmark_monthly === null) !== (rows.benchmark_cumulative === null);
  const ready = option !== undefined && missing.length === 0 && !benchHalf;
  const isProposal = recovery.proposal !== null && recovery.proposal.sheet === sheet && sameRows(rows, recovery.proposal.rows);

  const confirm = () => {
    if (!option) return;
    const dateRow = start && start.sheet === sheet ? start.date_row : option.date_row;
    onConfirm({ use: true, layout: { sheet, date_row: dateRow, rows } });
  };

  return (
    <div role="alert" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <p className="font-semibold text-amber-900">מאילו שורות בגיליון הסיכום לקרוא את התשואה של כל התיק?</p>
      <p className="mt-1 text-sm text-foreground">
        בגיליון הסיכום השורות לא נקראות בשמות הרגילים. בחרו את שורות התשואה החודשית והמצטברת (ואם יש — של הבנצ'מרק).
        המערכת בודקת שהתשואה החודשית, משורשרת חודש אחר חודש, מגיעה לתשואה המצטברת שבגיליון. הבחירה נשמרת ללקוח הזה בלבד,
        וההעלאה הבאה לא תשאל שוב. שום דבר לא נקלט עדיין.
      </p>
      {recovery.note && <p className="mt-2 text-sm text-amber-900">{recovery.note}</p>}
      {recovery.problems.length > 0 && (
        <ul className="mt-1 list-disc ps-5 text-sm text-destructive">
          {recovery.problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}

      <div className="mt-4 rounded border border-amber-200 bg-white p-3">
        {recovery.sheets.length > 1 ? (
          <label className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">גיליון:</span>
            <select aria-label="גיליון הסיכום" dir="rtl" value={sheet} onChange={(e) => pickSheet(e.target.value)}
              className="rounded-md border border-input bg-popover px-2 py-1 text-sm">
              {recovery.sheets.map((s) => <option key={s.sheet} value={s.sheet}>{s.sheet}</option>)}
            </select>
            <span className="text-xs text-muted-foreground">בקובץ יש {recovery.sheets.length} גיליונות שנראים כמו גיליון סיכום — בחרו את הנכון.</span>
          </label>
        ) : (
          <p className="text-sm"><span className="text-muted-foreground">גיליון: </span><span dir="auto" className="font-medium">{sheet}</span></p>
        )}
        {option && (
          <Table minWidth="18rem" className="mt-2">
            <THeadRow>
              <TH>סדרה</TH>
              <TH>שורה בגיליון</TH>
              <TH>הערכים האחרונים בשורה</TH>
            </THeadRow>
            <tbody>
              {SUMMARY_SERIES.map((s) => {
                const chosen = option.rows.find((r) => r.row === rows[s]);
                return (
                  <TR key={s}>
                    <TD>{SERIES_HE[s]}{REQUIRED.includes(s) && <span className="text-destructive"> *</span>}</TD>
                    <TD>
                      <select aria-label={`השורה של ${SERIES_HE[s]}`} dir="rtl" value={rows[s] ?? ''}
                        onChange={(e) => setRows((prev) => ({ ...prev, [s]: e.target.value === '' ? null : Number(e.target.value) }))}
                        className="w-full min-w-[9rem] max-w-[14rem] rounded-md border border-input bg-popover px-2 py-1 text-sm">
                        <option value="">{REQUIRED.includes(s) ? 'בחרו…' : 'אין בגיליון'}</option>
                        {option.rows.map((r) => <option key={r.row} value={r.row}>שורה {r.row} · {r.label}</option>)}
                      </select>
                    </TD>
                    <TD className="text-xs tabular-nums">
                      {chosen ? chosen.samples.map((v) => `${fmtMonth(v.date)}: ${fmtValue(v.value)}`).join(' · ') : <span className="text-muted-foreground">—</span>}
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        )}
        {isProposal && (
          <div className="mt-2 text-sm">
            {recovery.reason && <p className="text-muted-foreground" dir="auto">הצעת המערכת: {recovery.reason}</p>}
            {recovery.checks.map((c) => (
              <p key={c.series} className="text-emerald-700">
                ✓ {c.series === 'return' ? 'התשואה' : "הבנצ'מרק"} החודשית, משורשרת, מגיעה למצטברת שבגיליון ב-{c.matched} מתוך {c.pairs} חודשים
              </p>
            ))}
          </div>
        )}
        {!isProposal && ready && <p className="mt-2 text-xs text-muted-foreground">השורות שבחרתם ייבדקו באותה דרך לפני הקליטה.</p>}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" disabled={busy || !ready} onClick={confirm}
          className="rounded-md bg-primary px-6 py-2 font-semibold text-primary-foreground disabled:opacity-60">
          {busy ? 'קולט…' : 'אשרו את השורות וקלטו'}
        </button>
        <button type="button" disabled={busy} onClick={() => onConfirm({ use: false })}
          className="rounded-md border border-input px-4 py-2 text-sm font-semibold disabled:opacity-60">
          לא לקרוא גיליון סיכום — לחשב מהקרנות
        </button>
        <button type="button" disabled={busy} onClick={onCancel}
          className="rounded-md border border-input px-6 py-2 font-semibold disabled:opacity-60">
          ביטול
        </button>
        {!ready && option && (
          <span className="text-sm text-muted-foreground">
            {missing.length > 0 ? `בחרו שורה ל: ${missing.map((s) => SERIES_HE[s]).join(', ')}` : "לבנצ'מרק צריך גם שורה חודשית וגם מצטברת, או אף אחת"}
          </span>
        )}
      </div>
    </div>
  );
}
